import axios from "axios";
import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import "../../../assets/css/customer/shop/components/pl_ProductCard.css";
import AppContext from "../../../context/AppContext";
import ToastNotification from "../../../components/customer/shop/ToastNotification";

const ProductCard = ({ product }) => {
  const { baseUrl, authToken, baseUrlForFile, setCartCount, loggedInCustomer } =
    useContext(AppContext);
  const [toast, setToast] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [imageError, setImageError] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);

  // ── addToCart ─────────────────────────────────────────
  const addToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsAdding(true);

    const formdata = new FormData();
    formdata.append("adproduct_id", product.id);
    formdata.append("price", product.price);
    formdata.append("quantity", 1);
    formdata.append("type", "normal");
    formdata.append("total_price", product.price);
    formdata.append("adcustomer_id", loggedInCustomer?.id);

    axios
      .post(`${baseUrl}/customer/cart/createCartItem`, formdata, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setIsAdding(false);

        if (response.data.success) {
          setCartCount(response.data.cartCount);
          setToast({
            message: `${quantity}x ${product.name} added to cart successfully!`,
            type: "cart",
          });
        }
      })
      .catch((error) => {
        console.log("Cart Error:", error);
        setIsAdding(false);
      });
  };

  // ── addToWishlist ----
  const addToWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const formdata = new FormData();
    formdata.append("adproduct_id", product.id);

    axios
      .post(`${baseUrl}/customer/wishlist/addToWishlist`, formdata, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
        if (response.data.success) {
          setIsWishlisted(true);
        }
      })
      .catch((error) => {
        console.log("Error:", error);

        if (error.response) {
          console.log("Response Data:", error.response.data);
        }
      });
  };

  // ── removeFromWishlist —----
  const removeFromWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();

    axios
      .post(
        `${baseUrl}/customer/wishlist/removeFromWishlist`,
        { productId: product.id },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      )
      .then((response) => {
        console.log("remove from wishlist :", response.data);
        if (response.data.success) {
          setIsWishlisted(false);
        }
      })
      .catch((error) => {
        console.log("Wishlist remove Error:", error);
      });
  };

  // ── Toggle
  const toggleWishlistHandler = (e) => {
    if (isWishlisted) {
      removeFromWishlist(e);
    } else {
      addToWishlist(e);
    }
  };

  const handleImageError = () => setImageError(true);

  const discountPercentage = product.discount || 0;

  const formatPrice = (price) =>
    new Intl.NumberFormat("en-PK", {
      style: "currency",
      currency: "Pkr",
      maximumFractionDigits: 0,
    }).format(price);

  // ── JSX ───────────────────────────────────────────────
  return (
    <>
      {toast && (
        <ToastNotification
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="pl-product-card">
        <Link to={`/product/${product.id}`} className="pl-card-link">
          {/* Image Container */}
          <div className="pl-card-image-wrapper">
            <img
              src={
                imageError
                  ? "/images/placeholder.jpg"
                  : `${baseUrlForFile}${product.productimages[0]?.image_path}`
              }
              alt={product.name}
              className="pl-card-image"
              onError={handleImageError}
              loading="lazy"
            />

            {/* Badges */}
            <div className="pl-card-badges">
              {discountPercentage > 0 && (
                <span className="pl-badge pl-badge-sale">
                  {discountPercentage}% OFF
                </span>
              )}
              {product.featured && (
                <span className="pl-badge pl-badge-featured">Featured</span>
              )}
            </div>

            {/* Wishlist Button */}
            <button
              className={`pl-wishlist-btn ${isWishlisted ? "pl-wishlisted" : ""}`}
              onClick={toggleWishlistHandler}
              aria-label={
                isWishlisted ? "Remove from wishlist" : "Add to wishlist"
              }
            >
              <svg
                className="pl-heart-icon"
                viewBox="0 0 24 24"
                fill={isWishlisted ? "currentColor" : "none"}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
            </button>

            {/* Quick View Overlay */}
            <div className="pl-card-overlay">
              <button className="pl-quick-view-btn">
                <svg
                  className="pl-eye-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                Quick View
              </button>
            </div>
          </div>

          {/* Card Content */}
          <div className="pl-card-content">
            <p className="pl-card-category">{product.adcategory.name}</p>
            <h3 className="pl-card-title">{product.name}</h3>
            <p className="pl-card-description">{product.shortDesc}</p>

            {/* Rating */}
            <div className="pl-card-rating">
              <div className="pl-stars">
                {[...Array(5)].map((_, index) => (
                  <svg
                    key={index}
                    className={`pl-star ${index < Math.floor(product.rating) ? "pl-star-filled" : ""}`}
                    viewBox="0 0 24 24"
                    fill={
                      index < Math.floor(product.rating)
                        ? "currentColor"
                        : "none"
                    }
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                    />
                  </svg>
                ))}
              </div>
              <span className="pl-rating-count">({product.reviews})</span>
            </div>

            {/* Price */}
            <div className="pl-card-price">
              <span className="pl-price-current">
                {formatPrice(product.price)}
              </span>
              {product.originalPrice && (
                <span className="pl-price-original">
                  {formatPrice(product.originalPrice)}
                </span>
              )}
            </div>

            {/* Add to Cart Button */}
            <button
              className={`pl-add-cart-btn ${isAdding ? "pl-adding" : ""}`}
              onClick={addToCart}
            >
              {isAdding ? (
                <>
                  <svg
                    className="pl-cart-icon pl-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      strokeWidth="2"
                      opacity="0.25"
                    />
                    <path
                      d="M12 2a10 10 0 0110 10"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  Adding...
                </>
              ) : (
                <>Add to Cart</>
              )}
            </button>
          </div>
        </Link>
      </div>
    </>
  );
};

export default ProductCard;
